import { Col, Form, Row } from "reactstrap";
import { Btn, H3, LI, UL } from "../../../../../../AbstractElements";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import DollarInput from "../../../../../common/DollarInput";
import DropDown from "../../../../../common/DropDown";
import TextField from "../../../../../common/TextField";
import { Button } from "reactstrap";
import { LiabilitiesFormData, LiabilitiesFormErrors } from "../../../FormData";
import { useMutationRequestQuery } from "../../../../../../Hooks/useMutationRequestQuery";
import { getAllClientLiabilitiesByClientIdService, getClientLiabilitiesByIdService, insertOrUpdateClientLiabilitiesService, softDeleteClientLiabilitiesService, updateClientAssetsService } from "../../../../../../Services/PublicPageServices";
import { useDispatch } from "react-redux";
import { setFormStatus } from "../../../../../../ReduxToolkit/Reducers/FromStatusSlice";
import { Continue } from "../../../../../../Utils/Constants";
import { setFormsData } from "../../../../../../ReduxToolkit/Reducers/FormDataSlice";
import { useGetRequestQuery } from "../../../../../../Hooks/useGetRequestQuery";
import DataTable from "react-data-table-component";
import DecimalInput from "../../../../../common/DecimalInput";
import { replaceFormData } from "../../../../../../ReduxToolkit/Reducers/FactFindDataSlice";

export default function Liabilities({ activeCallBack, clientFactFindData, clientId }: any) {
  const [formData, setFormData] = useState<any>(LiabilitiesFormData);
  const [formErrors, setFormErrors] = useState<any>(LiabilitiesFormErrors);
  const [assetsPropertyData, setAssetsPropertyData] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const dispatch = useDispatch()

  // Validation functions
  const validateStringField = (value: string) => value.trim() !== ''; // For text-based fields
  const validateNumberField = (value: string) => value.trim() !== ''; // For numerical fields

  // Handle Error Change function to update the error state
  const handleErrorChange = (name: string, error: boolean) => {
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const {
    data: assetsProperty,
    refetch: refetchAssetsProperty,
    isLoading: assetsPropertyLoading,
    isError: assetsPropertyIsError,
  } = useGetRequestQuery(
    ['getAllClientLiabilitiesByClientIdService', clientId],
    () => getAllClientLiabilitiesByClientIdService(clientId),
    {
      enabled: true, // enable query only if clientId is available
    }
  );

  const {
    data: assetsPropertyFormData,
    isLoading: assetsPropertyFormDataLoading,
    isError: assetsPropertyFormDataIsError,
  } = useGetRequestQuery(
    ["getClientLiabilitiesByIdService", selectedRowId],
    () => getClientLiabilitiesByIdService(selectedRowId),
    {
      enabled: !!selectedRowId,
    }
  );

  useEffect(() => {
    if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
      setAssetsPropertyData(assetsProperty?.data);
      dispatch(
        replaceFormData({
          formName: "liabilities",
          formData: assetsProperty?.data,
        })
      );
    }
  }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);


  useEffect(() => {
    if (assetsPropertyFormData?.data && !assetsPropertyFormDataIsError && !assetsPropertyFormDataLoading) {
      if (assetsPropertyFormData?.data?.id) {
        setSelectedRowId(assetsPropertyFormData?.data?.id)
      }
      setFormData({
        liabilitiesLiabilityType: assetsPropertyFormData?.data?.liabilityType || "",
        liabilitiesLendersName: assetsPropertyFormData?.data?.lendersName || "",
        liabilitiesCurrentBalance: assetsPropertyFormData?.data?.currentBalance || "",
        liabilitiesCurrentLimit: assetsPropertyFormData?.data?.currentLimit || "",
        liabilitiesInterestRate: assetsPropertyFormData?.data?.liabilitiesInterestRate || "",
        liabilitiesRepayment: assetsPropertyFormData?.data?.repayment || "",
        liabilitiesRepaymentDLL: assetsPropertyFormData?.data?.frequency || "",
      })
    }
  }, [assetsPropertyFormData, assetsPropertyFormDataIsError, assetsPropertyFormDataLoading]);


  const { mutate: updateClientAssets, isPending: isUpdateClientAssetsPending } = useMutationRequestQuery(
    'insertOrUpdateClientLiabilitiesService',
    insertOrUpdateClientLiabilitiesService,
    {
      onSuccess: (data: any) => {
        toast.success("Liabilities information saved or updated successfully.");
        dispatch(setFormStatus({ id: '7', status: 7 }));
        // setFormErrors(LiabilitiesFormErrors);
        setFormData(LiabilitiesFormData)
        setSelectedRowId(null)
        refetchAssetsProperty()
      },
      onError: (error: any) => {
        toast.error("Failed to submit Liabilities information");
        console.error(error);
        setFormErrors(error?.response?.data || {});
      },
    }
  );

  const {
    mutate: deleteClientAssets,
    isPending: isDeleteClientAssetsPending,
  } = useMutationRequestQuery(
    'softDeleteClientLiabilitiesService',
    softDeleteClientLiabilitiesService,
    {
      onSuccess: (data: any) => {
        toast.success("Liabilitie deleted successfully.");
        refetchAssetsProperty()
        setSelectedRowId(null)
      },
      onError: (error: any) => {
        toast.error("Failed to delete property.");
        console.error(error);
      },
    }
  );

  useEffect(() => {
    dispatch(setFormsData({
      id: '7',
      formData: formData,
    }));
  }, [formData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate each liability field
    const liabilitiesLiabilityTypeValid = validateStringField(formData.liabilitiesLiabilityType);
    const liabilitiesLendersNameValid = validateStringField(formData.liabilitiesLendersName);
    const liabilitiesCurrentBalanceValid = validateNumberField(formData.liabilitiesCurrentBalance);
    const liabilitiesCurrentLimitValid = validateNumberField(formData.liabilitiesCurrentLimit);
    const liabilitiesRepaymentValid = validateNumberField(formData.liabilitiesRepayment);
    const liabilitiesRepaymentDLLValid = validateStringField(formData.liabilitiesRepaymentDLL);
    const liabilitiesInterestRateValid = validateStringField(formData.liabilitiesInterestRate);

    const isValid =
      liabilitiesLiabilityTypeValid &&
      liabilitiesLendersNameValid &&
      liabilitiesCurrentBalanceValid &&
      liabilitiesCurrentLimitValid &&
      liabilitiesRepaymentValid &&
      liabilitiesRepaymentDLLValid &&
      liabilitiesInterestRateValid;

    setFormErrors({
      liabilitiesLiabilityType: !liabilitiesLiabilityTypeValid,
      liabilitiesLendersName: !liabilitiesLendersNameValid,
      liabilitiesCurrentBalance: !liabilitiesCurrentBalanceValid,
      liabilitiesCurrentLimit: !liabilitiesCurrentLimitValid,
      liabilitiesRepayment: !liabilitiesRepaymentValid,
      liabilitiesRepaymentDLL: !liabilitiesRepaymentDLLValid,
      liabilitiesInterestRate: !liabilitiesInterestRateValid,
    });

    if (!isValid) {
      return;
    } else if (clientId != 0) {
      updateClientAssets({
        id: selectedRowId,
        clientId: clientId,
        liabilityType: formData.liabilitiesLiabilityType,
        lendersName: formData.liabilitiesLendersName,
        currentBalance: formData.liabilitiesCurrentBalance,
        currentLimit: formData.liabilitiesCurrentLimit,
        repayment: formData.liabilitiesRepayment,
        frequency: formData.liabilitiesRepaymentDLL,
        liabilitiesInterestRate: formData.liabilitiesInterestRate,
      })
    }
  };

  const handleValueChange = (name: string, value: string) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const splitDropdownValues = (dropdownValues: string) => {
    return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
  };

  const handleEditClick = (id: number) => {
    setSelectedRowId(id);
  };

  const handleDeleteClick = (id: number) => {
    deleteClientAssets(id)
  };

  const zeroConfigurationTableColumns = [
    {
      name: "Action",
      cell: (row: any) => (
        <UL className="action simple-list flex-row">
          <LI className="edit"><a onClick={() => { handleEditClick(row.id) }} href="#"><i className="icon-pencil-alt" /></a></LI>
          <LI className="delete"><a onClick={() => { handleDeleteClick(row.id) }} href="#"><i className="icon-trash" /></a></LI>
        </UL>
      ),
      style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Liability Type",
      selector: (row: any) => row["liabilityType"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Lender's Name",
      selector: (row: any) => row["lendersName"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Current Balance",
      selector: (row: any) => row["currentBalance"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Current Limit",
      selector: (row: any) => row["currentLimit"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Repayment",
      selector: (row: any) => row["repayment"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
    {
      name: "Frequency",
      selector: (row: any) => row["frequency"],
      sortable: false, style: {
        minWidth: '9rem', // Home Address column width set to 9rem
      }
    },
  ];


  return (
    <>
      <Form
        style={{
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          padding: "30px",
          backgroundColor: "#fff",
        }}
        onSubmit={handleSubmit}
      >
        {assetsPropertyData.length !== 0 ? <div style={{
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          padding: '5px',
          backgroundColor: '#fff',
        }}>
          <Row className='g-3 mb-4'>
            <Col xs={12} className='text-left'>
              <H3 className='f-w-900 heading-color'>Current Liabilities</H3>
            </Col>

            
          </Row>
          <DataTable
            title=""
            columns={zeroConfigurationTableColumns}
            data={assetsPropertyData}
            // pagination
            responsive
          />
        </div> : <div style={{
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          padding: '15px',
          backgroundColor: '#fff',
        }}>
          <Row className='g-3 mb-4'>
            <Col xs={12} className='text-left'>
              <H3 className='f-w-900 heading-color'>Current Liabilities</H3>
            </Col>
          </Row>
          No liabilities have been added yet.
        </div>
        }
        <Row className="g-3 mt-4">
          <Col xs={6} className="mb-4 text-left">
            <H3 className="f-w-900 heading-color">Liabilities</H3>
          </Col>
          <Col xs={6} className="text-end">
            {/* "Previous" Button */}
            <Btn color="primary" style={{ fontSize: '18px' }} className="me-2" onClick={() => activeCallBack(6)}>
              Previous
            </Btn>

            {/* "Continue" Button */}
            <Button type="button" color="success" style={{ fontSize: '18px' }} onClick={() => { activeCallBack(8) }}>
              {Continue}
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          {/* Liability Fields Column */}
          <Col sm={10} className='sm-row' >
            <DropDown
              label="Liability Type"
              name="liabilitiesLiabilityType"
              value={formData.liabilitiesLiabilityType}
              setValue={handleValueChange}
              options={splitDropdownValues(clientFactFindData ? clientFactFindData[15].dropDownValues : '')}
              // required={true}
              setError={handleErrorChange}
              errorMessage="Liability type is required"
              error={formErrors.liabilitiesLiabilityType}
              passCol="col-4"
              design="flex"
            />
          </Col>

          <Col sm={10} className='sm-row' >
            <TextField
              label="Lenders Name"
              type="text"
              name="liabilitiesLendersName"
              value={formData.liabilitiesLendersName}
              setValue={handleValueChange}
              placeholder="Enter lender's name"
              // required={true}
              setError={handleErrorChange}
              errorMessage="Lender's name is required"
              error={formErrors.liabilitiesLendersName}
              passCol="col-4"
              design="flex"
              maxChars={20}
            />
          </Col>

          <Col sm={10} className='sm-row' >
            <DollarInput
              label="Current Balance"
              name="liabilitiesCurrentBalance"
              value={formData.liabilitiesCurrentBalance}
              setValue={handleValueChange}
              placeholder="Enter current balance"
              // required={true}
              limit={6}
              setError={handleErrorChange}
              errorMessage="Current balance is required"
              error={formErrors.liabilitiesCurrentBalance}
              design="flex"
              passCol="col-2"
            />
          </Col>

          <Col sm={10} className='sm-row' >
            <DollarInput
              label="Current Limit"
              name="liabilitiesCurrentLimit"
              value={formData.liabilitiesCurrentLimit}
              setValue={handleValueChange}
              placeholder="Enter current limit"
              // required={true}
              limit={6}
              setError={handleErrorChange}
              errorMessage="Current limit is required"
              error={formErrors.liabilitiesCurrentLimit}
              design="flex"
              passCol="col-2"
            />
          </Col>
          <Col sm={10} className='sm-row' >

            <DecimalInput
              label="Interest Rate"
              name="liabilitiesInterestRate"
              value={formData.liabilitiesInterestRate}
              setValue={handleValueChange}
              placeholder=""
              // required={true}
              limit={4}
              setError={handleErrorChange}
              errorMessage="Interest rate is required"
              error={formErrors.liabilitiesInterestRate}
              design="flex"
              passCol="col-3"
            />
          </Col>
        </Row>

        <Row>
          <Col sm={10} className='sm-row' >
            <DollarInput
              label="Repayment"
              name="liabilitiesRepayment"
              value={formData.liabilitiesRepayment}
              setValue={handleValueChange}
              placeholder="Enter  amount"
              // required={true}
              limit={6}
              setError={handleErrorChange}
              errorMessage="Repayment amount is required"
              error={formErrors.liabilitiesRepayment}
              design="flex"
              passCol="col-2"
            />
          </Col>

          <Col sm={10} className='sm-row' >
            <DropDown
              label="Frequency"
              name="liabilitiesRepaymentDLL"
              value={formData.liabilitiesRepaymentDLL}
              setValue={handleValueChange}
              options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
              // required={true}
              setError={handleErrorChange}
              errorMessage="Repayment DLL is optional"
              error={formErrors.liabilitiesRepaymentDLL}
              passCol="col-4"
              design="flex"
            />
          </Col>
        </Row>
        <Button
          disabled={isUpdateClientAssetsPending}
          type="button"
          color="primary"
          onClick={handleSubmit}
          style={{ fontSize: '18px' }}
        >
          {isUpdateClientAssetsPending ? 'Loading . . .' : selectedRowId ? "Update Liability" : "Add New Liability"}
        </Button>

 
      </Form>
    </>
  );
}